import React, {
  FC,
  useEffect,
  useState,
  useCallback,
} from 'react';
import styled from 'styled-components';
import HelloSign from 'hellosign-embedded';
import { HelloSignModalProps } from './HelloSignModal.types';
import { useToaster, useAuthToken, usePrevious } from '../../hooks';
import { Modal, FullPageLoader } from '../index';
import { coreapi } from '../../api/core';

const Container = styled.div`
  iframe {
    height: 84vh;
  }
`;

function getContainerId(templateId) {
  return `hellosign-iframe-container-${templateId}`;
}

export const HelloSignModal: FC<HelloSignModalProps> = props => {
  const {
    onSign,
    defaultSigner,
    onClose,
    templateId,
    signers,
    fields,
    ccs,
    isOpen,
    ...modalProps
  } = props;

  const wasOpen = usePrevious(isOpen);
  const { authToken } = useAuthToken();
  const { toast, toaster } = useToaster();
  const [isLoading, setIsLoading] = useState(true);
  const [isCloseButtonVisible, setIsCloseButtonVisible] = useState(true);

  const setupHelloSignClient = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsCloseButtonVisible(true);

      const allSigners = signers && signers.length ? signers : [defaultSigner];

      const body = {
        fields: fields,
        signers: allSigners,
        ccs: ccs,
      };

      const response = await coreapi.post(
        authToken!,
        `hellosign/${templateId}`,
        body,
      );

      const signatureUrl = response.signature_url;

      const newClient = new HelloSign({
        clientId: process.env.REACT_APP_HELLOSIGN_CLIENT_ID,
      });

      const clientSettings = {
        testMode: process.env.NODE_ENV !== 'production',
        skipDomainVerification:
          process.env.REACT_APP_HELLOSIGN_TEST_MODE === 'true',
        container: document.getElementById(getContainerId(templateId)),
        redirectUrl: null,
        allowCancel: true,
        debug: false,
        uxVersion: 2,
      };

      newClient.on('sign', onSign);
      newClient.on('cancel', onClose);
      newClient.on('close', onClose);
      newClient.open(signatureUrl, clientSettings);

      setIsCloseButtonVisible(false);
    } catch (error) {
      toaster(toast.error(error, 'There was a problem loading the form.'));
      // if there some problem loading the form, show the close button just in case
      setIsCloseButtonVisible(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    fields,
    signers,
    ccs,
    onSign,
    onClose,
    authToken,
    templateId,
    toast,
    toaster,
    defaultSigner,
  ]);

  useEffect(() => {
    if (isOpen && !wasOpen) setupHelloSignClient();
  }, [isOpen, wasOpen, setupHelloSignClient]);

  return (
    <Modal
      {...modalProps}
      isOpen={isOpen}
      width="900px"
      isClosable={isCloseButtonVisible}
      onClose={() => {
        const container = document.getElementById(getContainerId(templateId));

        if (container) {
          while (container.firstChild) {
            container.firstChild.remove();
          }
        }

        onClose();
      }}
      data-testid="hellosign-modal"
      id="hellosign-modal"
    >
      {props.isOpen && (
        <>
          {isLoading && <FullPageLoader id="sign-modal-loader" />}
          <Container
            data-testid="hellosign-form"
            id={getContainerId(templateId)}
          />
        </>
      )}
    </Modal>
  );
};
